import _ from 'underscore';

import {
  ANY_REACTION_COMPONENT_ROLE_LABEL,
  MAX_REACTION_CHEM_SEARCH_CLAUSES,
} from 'chemistry/ChemistryConstants';
import {getNonEmptyChemicalStructureQuery, isEmptyChemicalStructureQuery} from 'chemistry/ChemSearchUtils';
import {ChemicalStructureDrawerValue} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {
  ChemistryReactionRole,
  CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME,
  CHEMISTRY_REACTION_ROLE_TO_SORT_ORDER,
} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {
  ChemicalStructureFilter,
  ChemicalStructureFilterFormat,
  isChemicalStructureFilter,
  SearchParams,
} from 'search/SearchConstants';

export interface ReactionSearchClauseDraft {
  id: string;
  chemicalStructureQuery: ChemicalStructureDrawerValue;
  minSimilarity: number;
  reactionComponentRole: ChemistryReactionRole | null;
}

export interface ReactionSearchClause {
  chemicalStructureQuery: string;
  minSimilarity: number;
  reactionComponentRole: ChemistryReactionRole | null;
}

export type ReactionSearchClauseValidationError =
  | {type: 'empty_structure'; clauseId: string; clauseIndex: number}
  | {type: 'no_clauses'}
  | {type: 'too_many_clauses'};

export type ValidateReactionSearchClauseDraftsResult =
  | {type: 'success'; clauses: Array<ReactionSearchClause>}
  | {type: 'error'; error: ReactionSearchClauseValidationError};

export function createReactionSearchClauseDraft(
  draft: Omit<ReactionSearchClauseDraft, 'id'>
): ReactionSearchClauseDraft {
  return {id: _.uniqueId('reaction-search-clause-'), ...draft};
}

/** Roles shown in chem search UI; matches stoichiometry table. */
export const REACTION_COMPONENT_ROLE_UI_ORDER: Array<ChemistryReactionRole> = Object.values(
  ChemistryReactionRole
).sort((a, b) => CHEMISTRY_REACTION_ROLE_TO_SORT_ORDER[a] - CHEMISTRY_REACTION_ROLE_TO_SORT_ORDER[b]);

const UI_REACTION_COMPONENT_ROLES = new Set<string>(Object.values(ChemistryReactionRole));

export function parseChemistryReactionRole(role: string | null | undefined): ChemistryReactionRole | null {
  if (role != null && UI_REACTION_COMPONENT_ROLES.has(role)) {
    return role as ChemistryReactionRole;
  }
  return null;
}

export function validateReactionSearchClauseDrafts(
  clauses: Array<ReactionSearchClauseDraft>
): ValidateReactionSearchClauseDraftsResult {
  if (clauses.length === 0) {
    return {type: 'error', error: {type: 'no_clauses'}};
  }
  if (clauses.length > MAX_REACTION_CHEM_SEARCH_CLAUSES) {
    return {type: 'error', error: {type: 'too_many_clauses'}};
  }
  const validatedClauses: Array<ReactionSearchClause> = [];
  for (let clauseIndex = 0; clauseIndex < clauses.length; clauseIndex++) {
    const clause = clauses[clauseIndex];
    if (isEmptyChemicalStructureQuery(clause.chemicalStructureQuery)) {
      return {type: 'error', error: {type: 'empty_structure', clauseId: clause.id, clauseIndex}};
    }
    validatedClauses.push({
      chemicalStructureQuery: getNonEmptyChemicalStructureQuery(clause.chemicalStructureQuery),
      minSimilarity: clause.minSimilarity,
      reactionComponentRole: clause.reactionComponentRole,
    });
  }
  return {type: 'success', clauses: validatedClauses};
}

export function getReactionSearchClauseValidationMessage(
  error: ReactionSearchClauseValidationError,
  clauseCount: number
): string {
  if (error.type === 'no_clauses') {
    return 'Draw a chemical structure to search.';
  }
  if (error.type === 'too_many_clauses') {
    return `Chemical structure search supports up to ${MAX_REACTION_CHEM_SEARCH_CLAUSES} structures.`;
  }
  if (clauseCount === 1) {
    return 'Draw a chemical structure to search.';
  }
  return `Draw a chemical structure for Molecule ${error.clauseIndex + 1}.`;
}

export function getReactionComponentRoleOptionLabel(role: ChemistryReactionRole | null): string {
  if (role == null) {
    return ANY_REACTION_COMPONENT_ROLE_LABEL;
  }
  return CHEMISTRY_REACTION_ROLE_TO_DISPLAY_NAME[role];
}

export function getInitialReactionSearchClauses(
  searchParams: SearchParams,
  initialMol: ChemicalStructureDrawerValue,
  defaultMinSimilarity: number
): Array<ReactionSearchClauseDraft> {
  const existingClauses = searchParams.filters
    .filter(
      (filter): filter is ChemicalStructureFilter =>
        isChemicalStructureFilter(filter) && filter.format === ChemicalStructureFilterFormat.MOL
    )
    .slice(0, MAX_REACTION_CHEM_SEARCH_CLAUSES)
    .map((filter) =>
      createReactionSearchClauseDraft({
        chemicalStructureQuery: filter.value,
        minSimilarity: filter.minSimilarity ?? defaultMinSimilarity,
        reactionComponentRole: parseChemistryReactionRole(filter.reactionComponentRole),
      })
    );
  if (existingClauses.length > 0) {
    return existingClauses;
  }
  return [
    createReactionSearchClauseDraft({
      chemicalStructureQuery: initialMol,
      minSimilarity: defaultMinSimilarity,
      reactionComponentRole: null,
    }),
  ];
}
